// src/modules/requerimiento/views/devolucion/ConfirmarTransferencia.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  List, ListItem, ListItemText, Checkbox, Stack, FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getTransferenciaPreview, postConfirmarTransferencia } from "../../services/devolucion";

export default function ConfirmarTransferencia({ cod, onToast, onRefreshRequerimiento }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedPlacas, setSelectedPlacas] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [resultado, setResultado] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getTransferenciaPreview(cod);
      const payload = data?.data || data;
      setPreview(payload);
      const todasLasPlacas = (payload?.placas || []).map((p) => p.placa);
      setSelectedPlacas(new Set(todasLasPlacas));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo cargar la vista previa de transferencia.");
    } finally {
      setLoading(false);
    }
  }, [cod]);

  useEffect(() => { cargar(); }, [cargar]);

  const togglePlaca = (placa) => {
    setSelectedPlacas((prev) => {
      const next = new Set(prev);
      if (next.has(placa)) next.delete(placa);
      else next.add(placa);
      return next;
    });
  };

  const handleConfirmar = async () => {
    // Un modem puede aparecer mas de una vez por placa (una fila por cada chip
    // vinculado) — sin el Set, un num_sim repetido se procesaria dos veces en el
    // backend, y la segunda pasada fallaria porque la primera ya cerro (Finalizado)
    // la relacion rel_modem_servicio con el cliente origen.
    const numSim = [...new Set(
      (preview?.placas || [])
        .filter((p) => selectedPlacas.has(p.placa))
        .flatMap((p) => (p.modems || []).map((m) => String(m.num_sim)))
    )];

    if (!numSim.length) {
      onToast?.({ sev: "warning", msg: "Selecciona al menos una placa para transferir." });
      return;
    }
    setConfirming(true);
    try {
      const { data } = await postConfirmarTransferencia(cod, { num_sim: numSim });
      const payload = data?.data || data;
      setResultado(payload);
      onToast?.({ sev: "success", msg: "Transferencia confirmada." });
      await onRefreshRequerimiento?.();
    } catch (e) {
      onToast?.({
        sev: "error",
        msg: e?.response?.data?.message || e?.message || "No se pudo confirmar la transferencia.",
      });
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  const destinoNombre = preview?.reqInfo?.razon_social_destino
    || `Servicio #${preview?.reqInfo?.cod_servicio_destino}`;
  const totalPlacas = (preview?.placas || []).length;

  if (resultado) {
    return (
      <Paper variant="outlined" sx={{ p: 3, mt: 2, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Transferencia confirmada
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Se cerró esta devolución y se creó el requerimiento de Adición #{resultado.cod_adicion} para {destinoNombre}.
        </Typography>
        <Button variant="contained" onClick={() => navigate(`/requerimiento/${resultado.cod_adicion}`)}>
          Ver requerimiento de Adición #{resultado.cod_adicion}
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Confirmar transferencia de cliente
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cliente destino: <strong>{destinoNombre}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Selecciona las placas que se van a transferir — al marcar una placa se incluyen todos sus
          módems, no se pueden transferir por separado. Al confirmar: se corta el vínculo con el
          cliente actual (sin devolución física), esta devolución se cierra, y se crea automáticamente
          un requerimiento de Adición ya vinculado para el cliente destino.
        </Typography>
      </Paper>

      {(preview?.placas || []).map(({ placa, modems }) => (
        <Paper key={placa} variant="outlined" sx={{ mb: 1.5, overflow: "hidden" }}>
          <Box sx={{ px: 1, py: 0.5, bgcolor: "action.hover" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedPlacas.has(placa)}
                  onChange={() => togglePlaca(placa)}
                />
              }
              label={<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{placa}</Typography>}
            />
          </Box>
          <List dense disablePadding>
            {(!modems || modems.length === 0) && (
              <ListItem>
                <ListItemText primary="Sin módems encontrados para esta placa." />
              </ListItem>
            )}
            {(modems || []).map((modem) => {
              const numSim = String(modem.num_sim);
              return (
                <ListItem key={numSim} divider sx={{ pl: 5, opacity: selectedPlacas.has(placa) ? 1 : 0.5 }}>
                  <ListItemText
                    primary={numSim}
                    secondary={[
                      modem.numero_chip ? `Chip: ${modem.numero_chip}` : null,
                      modem.estado ? `Estado: ${modem.estado}` : null,
                    ].filter(Boolean).join(" · ")}
                  />
                </ListItem>
              );
            })}
          </List>
        </Paper>
      ))}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {selectedPlacas.size} de {totalPlacas} placas seleccionadas
        </Typography>
        <Button variant="contained" disabled={confirming || !selectedPlacas.size} onClick={handleConfirmar}>
          {confirming ? "Confirmando..." : "Confirmar transferencia"}
        </Button>
      </Stack>
    </Box>
  );
}
